import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { CompanyData } from '../../types/inpi';
import { formatDate } from '../../utils/inseeCodeMappings';

interface DirectorsCardProps {
  companyData: CompanyData | null;
  isLoading: boolean;
  error: string | null;
}

const DirectorsCard: React.FC<DirectorsCardProps> = ({ companyData, isLoading, error }) => {
  // Helper to check if data is for entrepreneur individuel
  const isEntrepreneurIndividuel = () => {
    return companyData?.formality?.typePersonne === 'P' || 
           companyData?.formality?.content?.personnePhysique !== undefined;
  };
  
  // Helper to get entrepreneur info as a director
  const getEntrepreneurAsDirector = () => {
    if (!companyData || !isEntrepreneurIndividuel()) return null;
    
    const personnePhysique = companyData.formality?.content?.personnePhysique;
    if (!personnePhysique?.identite?.entrepreneur?.descriptionPersonne) return null;
    
    const personne = personnePhysique.identite.entrepreneur.descriptionPersonne;
    const nom = personne.nom || '';
    const prenoms = personne.prenoms ? personne.prenoms.join(' ') : '';
    
    return {
      nom: nom,
      prenom: prenoms,
      qualite: 'Entrepreneur individuel',
      date_naissance: personne.dateDeNaissance || '',
      type: 'Personne physique',
      actif: true
    };
  };
  
  if (error) {
    return (
      <div>
        <p className="text-sm text-gray-600">Données temporairement indisponibles</p>
      </div>
    );
  }

  // For entrepreneurs individuels, create a single entry for the entrepreneur
  const entrepreneurDirector = isEntrepreneurIndividuel() ? getEntrepreneurAsDirector() : null;
  
  // Use either the entrepreneur as director or the company dirigeants
  const dirigeants = entrepreneurDirector ? [entrepreneurDirector] : (companyData?.dirigeants || []);
  const hasDirigeants = dirigeants.length > 0;

  return (
    <div>
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton height={24} width="50%" />
          <Skeleton count={4} />
        </div>
      ) : hasDirigeants ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prénom
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qualité
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de naissance
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dirigeants.map((dirigeant, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {dirigeant.nom || 'Non renseigné'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {dirigeant.prenom || 'Non renseigné'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {dirigeant.qualite || 'Non renseigné'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {dirigeant.date_naissance ? formatDate(dirigeant.date_naissance) : 'Non renseigné'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {dirigeant.type || 'Non renseigné'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {dirigeant.actif ? 
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Actif
                      </span> : 
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Ancien
                      </span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-600">Aucun dirigeant disponible</p>
      )}
    </div>
  );
};

export default DirectorsCard; 